/**
 * Attaches PER-VERTEX cloth (clothPbd.ts) to a mounted garment, as an alternative to the
 * bone-chain cage in cloth.ts. Both paths ship; this one is opt-in (see Avatar.setPbdCloth).
 *
 * The garment arrives as ONE SkinnedMesh holding two different things: a worn part (collar,
 * shoulder shawl) skinned to body bones, and a simulated part skinned to the generated
 * `cl_*` cage bones. GPU skinning would fight anything we wrote into the vertex buffer, so the
 * simulated triangles are SPLIT OUT into a plain Mesh whose positions we own:
 *
 *   skinned mesh  →  keeps only triangles with NO cage vertex (the worn part, GPU-skinned)
 *   cape mesh     →  every triangle touching a cage vertex, simulated per vertex
 *
 * The two meet along a ring of vertices that belong to both. Those are PINNED: every frame they
 * are driven to the exact position the skinned mesh gives them, so the seam is welded by
 * construction and cannot crack, however hard the sheet swings.
 *
 * The cage bones are left in the rig and simply idle — nothing is skinned to them on this path,
 * so the bone-chain sim can be switched back on without rebuilding the asset.
 */
import * as THREE from "three";
import { PbdCloth, type PbdCapsule, type ClothMeshCollider } from "./clothPbd";

export interface PbdCapeOpts {
  /** Bone-name predicate identifying the SIMULATED region (the generated cage bones). */
  isCageBone: (name: string) => boolean;
  /** Collider set, already resolved to bones. */
  capsules: PbdCapsule[];
}

const _w = new THREE.Vector3();
const _l = new THREE.Vector3();
const _inv = new THREE.Matrix4();

/** Height band (m) below the cape's top edge that is pinned as the suspension line. */
const SUSPEND_BAND = 0.045;

export class PbdCape {
  readonly mesh: THREE.Mesh;
  /** Public so Avatar.pbdClothStats() can read the contact-generation accumulators. */
  readonly sim: PbdCloth;
  /** cape-geometry vertex -> source-geometry vertex */
  private readonly srcOf: Uint32Array;
  /** cape-geometry vertices that are pinned to the skinned surface */
  private readonly pins: Uint32Array;
  /** rest configuration (world) for reseed */
  private readonly rest: Float32Array;
  private readonly posAttr: THREE.BufferAttribute;

  constructor(
    private readonly src: THREE.SkinnedMesh,
    geom: THREE.BufferGeometry,
    srcOf: Uint32Array,
    pins: Uint32Array,
    edges: Uint32Array,
    bends: Uint32Array,
    bindWorld: Float32Array,
    capsules: PbdCapsule[],
  ) {
    this.srcOf = srcOf;
    this.pins = pins;
    this.rest = Float32Array.from(bindWorld);
    this.posAttr = geom.attributes.position as THREE.BufferAttribute;
    this.sim = new PbdCloth(bindWorld, edges, bends, pins);
    this.sim.setCapsules(capsules);

    this.mesh = new THREE.Mesh(geom, src.material);
    this.mesh.name = "pbd-cape"; // stable handle for probes/debug — do not rename
    this.mesh.frustumCulled = false;
    this.mesh.castShadow = src.castShadow;
    this.mesh.receiveShadow = src.receiveShadow;
    this.mesh.userData.avatarShadowDefault = src.userData.avatarShadowDefault;
    this.mesh.matrixAutoUpdate = false; // lives in the source mesh's parent space
    src.parent?.add(this.mesh);
  }

  setCapsules(caps: PbdCapsule[]): void { this.sim.setCapsules(caps); }

  /** Swap the full collider set (Avatar.refreshPbdColliders — outfit or body changed, or the
   *  BVH flag flipped). Slack re-measures against the current pose, like setCapsules. */
  setColliders(caps: PbdCapsule[], meshes: { col: ClothMeshCollider; margin: number }[]): void {
    this.sim.setCapsules(caps);
    this.sim.setMeshColliders(meshes);
  }

  setShadows(on: boolean): void {
    this.mesh.castShadow = on && this.mesh.userData.avatarShadowDefault !== false;
  }

  /** Re-seat the sheet at the current pose (spawn / teleport / LOD-in). */
  reseed(): void {
    this.driveRest();
    this.sim.reseed(this.rest);
    this.driveRest();
  }

  /** Refresh the authored drape for EVERY cape vertex from the garment's own skinning, and drive
   *  the pinned ones onto it. The cage bones idle on this path, so a cage vertex's skinned
   *  position is the authored drape rigidly carried by the body — exactly the target shape
   *  retention wants, and it costs one bone transform per vertex. */
  private driveRest(): void {
    for (let i = 0; i < this.sim.count; i++) {
      this.src.getVertexPosition(this.srcOf[i], _w);
      this.src.localToWorld(_w);
      this.rest[i * 3] = _w.x; this.rest[i * 3 + 1] = _w.y; this.rest[i * 3 + 2] = _w.z;
    }
    for (let k = 0; k < this.pins.length; k++) {
      const i = this.pins[k];
      _w.set(this.rest[i * 3], this.rest[i * 3 + 1], this.rest[i * 3 + 2]);
      this.sim.setPinnedPosition(i, _w);
    }
  }

  update(dt: number, down: THREE.Vector3, frame: THREE.Object3D | null, wind: THREE.Vector3 | null, waterK: number): void {
    // ORDER MATTERS: carry the state into this frame's co-rotating pose FIRST, then drive the
    // pins to their exact skinned positions — see PbdCloth.beginFrame for the flail this
    // ordering prevents. (The caller has already refreshed frame.matrixWorld.)
    this.sim.beginFrame(frame);
    this.driveRest();
    this.sim.step(dt, down, wind, waterK, this.rest);
    // world -> the parent space the cape mesh is drawn in
    const parent = this.mesh.parent;
    if (parent) _inv.copy(parent.matrixWorld).invert(); else _inv.identity();
    const out = this.posAttr.array as Float32Array;
    const p = this.sim.pos;
    for (let i = 0; i < this.sim.count; i++) {
      _l.set(p[i * 3], p[i * 3 + 1], p[i * 3 + 2]).applyMatrix4(_inv);
      out[i * 3] = _l.x; out[i * 3 + 1] = _l.y; out[i * 3 + 2] = _l.z;
    }
    this.posAttr.needsUpdate = true;
    this.mesh.geometry.computeVertexNormals(); // folds emerge per frame — normals must follow
  }

  dispose(): void {
    this.mesh.removeFromParent();
    this.mesh.geometry.dispose();
  }
}

/**
 * Split the simulated region out of a mounted garment and return a per-vertex cape for it, or
 * null when this garment has no simulated region (every other piece in the set).
 *
 * ⚠ The source geometry is a SHARED template across avatars — never mutate it. The skinned mesh
 * gets its own index buffer here; everything else stays shared.
 */
export function attachPbdCape(src: THREE.SkinnedMesh, opts: PbdCapeOpts): PbdCape | null {
  const geo = src.geometry;
  const pos = geo.attributes.position as THREE.BufferAttribute;
  const skinIdx = geo.attributes.skinIndex as THREE.BufferAttribute | undefined;
  const skinW = geo.attributes.skinWeight as THREE.BufferAttribute | undefined;
  const index = geo.index;
  if (!skinIdx || !skinW || !index) return null;

  const names = src.skeleton.bones.map((b) => b.name);
  const cage = new Set<number>();
  names.forEach((n, i) => { if (opts.isCageBone(n)) cage.add(i); });
  if (!cage.size) return null;

  const isCageVert = new Uint8Array(pos.count);
  let cageVerts = 0;
  const comp = ["getX", "getY", "getZ", "getW"] as const;
  for (let i = 0; i < pos.count; i++) {
    for (const c of comp) {
      if (skinW[c](i) > 0 && cage.has(skinIdx[c](i))) { isCageVert[i] = 1; cageVerts++; break; }
    }
  }
  if (!cageVerts) return null;

  // triangles touching the cage go to the cape; the rest stay GPU-skinned
  const capeTris: number[] = [], keepTris: number[] = [];
  for (let t = 0; t < index.count; t += 3) {
    const a = index.getX(t), b = index.getX(t + 1), c = index.getX(t + 2);
    (isCageVert[a] || isCageVert[b] || isCageVert[c] ? capeTris : keepTris).push(a, b, c);
  }
  if (!capeTris.length) return null;

  // ---- cape geometry: copy the touched vertices, remapped
  const remap = new Int32Array(pos.count).fill(-1);
  const srcOf: number[] = [];
  for (const v of capeTris) if (remap[v] < 0) { remap[v] = srcOf.length; srcOf.push(v); }
  const n = srcOf.length;

  const capeGeo = new THREE.BufferGeometry();
  const cp = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    const s = srcOf[i];
    cp[i * 3] = pos.getX(s); cp[i * 3 + 1] = pos.getY(s); cp[i * 3 + 2] = pos.getZ(s);
  }
  capeGeo.setAttribute("position", new THREE.BufferAttribute(cp, 3));
  capeGeo.setAttribute("normal", new THREE.BufferAttribute(new Float32Array(n * 3), 3));
  for (const name of ["color", "uv"] as const) {
    const a = geo.attributes[name] as THREE.BufferAttribute | undefined;
    if (!a) continue;
    const itemSize = a.itemSize;
    const dst = new Float32Array(n * itemSize);
    for (let i = 0; i < n; i++) {
      for (let k = 0; k < itemSize; k++) dst[i * itemSize + k] = a.array[srcOf[i] * itemSize + k] as number;
    }
    // COLOR_0 ships normalized-uint8 on these assets; re-emit as float to keep the paint exact
    const norm = a.normalized ? 255 : 1;
    if (norm !== 1) for (let k = 0; k < dst.length; k++) dst[k] /= norm;
    capeGeo.setAttribute(name, new THREE.BufferAttribute(dst, itemSize));
  }
  capeGeo.setIndex(capeTris.map((v) => remap[v]));

  // ---- pins. Two groups, and BOTH are needed:
  //  1. the shared seam ring — cape triangles that also touch the worn part. Driving these to
  //     the skinned surface welds the two meshes so the seam cannot crack.
  //  2. the SUSPENSION edge — the cape's top row. On the cage path the whole top row is skinned
  //     to the chain ROOT bones, so it is cage-weighted and would land in group (1)'s complement
  //     and hang free: the sheet then dangles from its side seams alone and collapses into a
  //     bundle (which is exactly what the first build did). A cape has to be attached along its
  //     top. Those cage bones still ride spine_05 rigidly when the chain sim is off, so the
  //     skinned position is a correct, animated attachment line.
  const pins: number[] = [];
  // ⚠ measure the top over CAGE verts only. The cape geometry also holds the seam ring, which
  // reaches up the collar well above the cape's own top row — measuring over everything puts the
  // band above every cage vertex, pins nothing, and the sheet collapses exactly as before.
  let maxY = -Infinity;
  for (let i = 0; i < n; i++) if (isCageVert[srcOf[i]]) maxY = Math.max(maxY, cp[i * 3 + 1]);
  for (let i = 0; i < n; i++) {
    if (!isCageVert[srcOf[i]] || cp[i * 3 + 1] > maxY - SUSPEND_BAND) pins.push(i);
  }

  // ---- constraints
  const edgeSet = new Set<number>();
  const edges: number[] = [];
  const addEdge = (i: number, j: number) => {
    const k = i < j ? i * n + j : j * n + i;
    if (edgeSet.has(k)) return;
    edgeSet.add(k); edges.push(i, j);
  };
  // bending: for each interior edge, connect the two opposite corners of its adjacent triangles
  const opposite = new Map<number, number>();
  const bends: number[] = [];
  const tri = capeTris.map((v) => remap[v]);
  for (let t = 0; t < tri.length; t += 3) {
    const v = [tri[t], tri[t + 1], tri[t + 2]];
    for (let e = 0; e < 3; e++) {
      const i = v[e], j = v[(e + 1) % 3], o = v[(e + 2) % 3];
      addEdge(i, j);
      const k = i < j ? i * n + j : j * n + i;
      const prev = opposite.get(k);
      if (prev === undefined) opposite.set(k, o);
      else if (prev !== o) bends.push(prev, o);
    }
  }

  // ---- bind positions in WORLD space (the sim runs there, like cloth.ts)
  src.updateWorldMatrix(true, false);
  const bindWorld = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    _w.set(cp[i * 3], cp[i * 3 + 1], cp[i * 3 + 2]);
    src.localToWorld(_w);
    bindWorld[i * 3] = _w.x; bindWorld[i * 3 + 1] = _w.y; bindWorld[i * 3 + 2] = _w.z;
  }

  // ---- the skinned mesh keeps only the untouched triangles, on its OWN index (shared geometry!)
  const own = src.geometry.clone();
  own.setIndex(keepTris);
  src.geometry = own;

  const cape = new PbdCape(src, capeGeo, Uint32Array.from(srcOf), Uint32Array.from(pins),
    Uint32Array.from(edges), Uint32Array.from(bends), bindWorld, opts.capsules);
  capeGeo.computeVertexNormals();
  return cape;
}
